import React, { useEffect, useState } from 'react';
import {
    Autocomplete,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormHelperText,
    Stack,
    TextField,
} from '@mui/material';
import { BulkSwapRequest } from '../../interfaces/shift/RosterCell';
import { ShiftRosterService } from '../../services/shift-roster.service';
import { useToast } from '../../context/ToastContext';
import { useTranslation } from 'react-i18next';

const KNOWN_SWAP_ERRORS = {
    INVALID_TIME_RANGE: 'bulkSwap.errors.invalidRange',
    SAME_USER_SWAP: 'bulkSwap.errors.sameUser',
    SWAP_BLOCKED_ATTENDANCE_PRESENT: 'bulkSwap.errors.attendancePresent',
} as const;

type SwapErrorKey = typeof KNOWN_SWAP_ERRORS[keyof typeof KNOWN_SWAP_ERRORS] | 'bulkSwap.errors.failed';

/** Maps a known backend error code to a translation key; other backend text is shown as sent. */
const parseSwapError = (err: unknown): { key: SwapErrorKey } | { message: string } => {
    const axErr = err as any;
    const msg: string = axErr?.response?.data?.message || axErr?.response?.data || axErr?.message || '';
    for (const [code, key] of Object.entries(KNOWN_SWAP_ERRORS)) {
        if (msg.includes(code)) return { key };
    }
    return msg ? { message: msg } : { key: 'bulkSwap.errors.failed' };
};

const todayStr = () => new Date().toISOString().slice(0, 10);

interface SimpleUser {
    id: string;
    username: string;
    name?: string;
}

interface Props {
    open: boolean;
    onClose: () => void;
    institutionId: string;
    users: SimpleUser[];
    onSaved: () => void;
}

const BulkSwapDialog: React.FC<Props> = ({ open, onClose, institutionId, users, onSaved }) => {
    const toast = useToast();
    const { t } = useTranslation(['shifts', 'common']);

    const [userA, setUserA] = useState<SimpleUser | null>(null);
    const [userB, setUserB] = useState<SimpleUser | null>(null);
    const [fromDate, setFromDate] = useState(todayStr());
    const [toDate, setToDate]     = useState(todayStr());
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState<{
        userA?: string; userB?: string; fromDate?: string; toDate?: string;
    }>({});

    useEffect(() => {
        if (open) {
            setUserA(null);
            setUserB(null);
            setFromDate(todayStr());
            setToDate(todayStr());
            setSaving(false);
            setErrors({});
        }
    }, [open]);

    const displayUser = (u: SimpleUser) => u.name || u.username;

    const validate = (): boolean => {
        const errs: typeof errors = {};
        if (!userA) errs.userA = t('bulkSwap.selectUserA');
        if (!userB) errs.userB = t('bulkSwap.selectUserB');
        if (userA && userB && userA.id === userB.id) {
            errs.userB = t('bulkSwap.usersMustDiffer');
        }
        if (!fromDate) errs.fromDate = t('bulkSwap.fromRequired');
        if (!toDate)   errs.toDate   = t('bulkSwap.toRequired');
        if (fromDate && toDate && fromDate > toDate) {
            errs.toDate = t('bulkSwap.toBeforeFrom');
        }
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            const body: BulkSwapRequest = {
                userIdA: userA!.id,
                userIdB: userB!.id,
                fromDate,
                toDate,
            };
            const result = await ShiftRosterService.bulkSwap(institutionId, body);
            toast.showSuccess(t('bulkSwap.swapped', { count: result.length }));
            onSaved();
            onClose();
        } catch (err) {
            const parsed = parseSwapError(err);
            toast.showApiError(err, 'key' in parsed ? t(parsed.key) : parsed.message);
        } finally {
            setSaving(false);
        }
    };

    // Options for B: exclude user A
    const optionsB = userA ? users.filter(u => u.id !== userA.id) : users;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{t('bulkSwap.title')}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    {/* User A */}
                    <Autocomplete
                        options={users}
                        value={userA}
                        onChange={(_e, val) => { setUserA(val); setErrors(prev => ({ ...prev, userA: undefined })); }}
                        getOptionLabel={displayUser}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        renderInput={params => (
                            <TextField
                                {...params}
                                label={t('bulkSwap.userA')}
                                required
                                error={!!errors.userA}
                                helperText={errors.userA}
                                inputProps={{ ...params.inputProps, 'data-testid': 'bulk-swap-userA' }}
                            />
                        )}
                        data-testid="bulk-swap-userA-auto"
                    />

                    {/* User B */}
                    <Autocomplete
                        options={optionsB}
                        value={userB}
                        onChange={(_e, val) => { setUserB(val); setErrors(prev => ({ ...prev, userB: undefined })); }}
                        getOptionLabel={displayUser}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        renderInput={params => (
                            <TextField
                                {...params}
                                label={t('bulkSwap.userB')}
                                required
                                error={!!errors.userB}
                                helperText={errors.userB}
                                inputProps={{ ...params.inputProps, 'data-testid': 'bulk-swap-userB' }}
                            />
                        )}
                        data-testid="bulk-swap-userB-auto"
                    />

                    {/* Date range */}
                    <TextField
                        label={t('common:fields.from')}
                        type="date"
                        fullWidth
                        value={fromDate}
                        onChange={e => { setFromDate(e.target.value); setErrors(prev => ({ ...prev, fromDate: undefined })); }}
                        error={!!errors.fromDate}
                        helperText={errors.fromDate}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ 'data-testid': 'bulk-swap-from' }}
                    />
                    <TextField
                        label={t('common:fields.to')}
                        type="date"
                        fullWidth
                        value={toDate}
                        onChange={e => { setToDate(e.target.value); setErrors(prev => ({ ...prev, toDate: undefined })); }}
                        error={!!errors.toDate}
                        helperText={errors.toDate}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ 'data-testid': 'bulk-swap-to' }}
                    />

                    {/* General error placeholder (shown via toast) */}
                    {errors.userA && !userA && (
                        <FormHelperText error>{errors.userA}</FormHelperText>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>{t('common:actions.cancel')}</Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={saving}
                    data-testid="bulk-swap-save-btn"
                >
                    {saving ? t('bulkSwap.swapping') : t('bulkSwap.swap')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BulkSwapDialog;
